import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Lock, Eye, EyeOff, Loader2, CheckCircle, AlertCircle, ArrowLeft } from 'lucide-react';
import IvisoraLogo from '../components/shared/IvisoraLogo';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const ResetPasswordPage = () => {
  const navigate = useNavigate();
  const token = new URLSearchParams(window.location.search).get('token') || '';

  const [newPassword, setNewPassword]     = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showNew, setShowNew]             = useState(false);
  const [showConfirm, setShowConfirm]     = useState(false);
  const [status, setStatus]               = useState('idle'); // idle | loading | success | error
  const [errorMsg, setErrorMsg]           = useState('');

  // Redirect after success
  const handleSuccess = () => {
    setTimeout(() => navigate('/'), 3000);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErrorMsg('');

    if (!token) {
      setErrorMsg('Missing reset token. Please use the link from your email.');
      setStatus('error');
      return;
    }
    if (newPassword.length < 8) {
      setErrorMsg('Password must be at least 8 characters.');
      setStatus('error');
      return;
    }
    if (newPassword !== confirmPassword) {
      setErrorMsg('Passwords do not match.');
      setStatus('error');
      return;
    }

    setStatus('loading');
    try {
      const res = await fetch(`${API_URL}/api/auth/reset-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token, new_password: newPassword }),
      });
      const data = await res.json().catch(() => ({}));

      if (!res.ok) {
        setErrorMsg(data.detail || 'Something went wrong. Please try again.');
        setStatus('error');
        return;
      }

      setStatus('success');
      handleSuccess();
    } catch {
      setErrorMsg('Network error. Please check your connection and try again.');
      setStatus('error');
    }
  };

  const strength = (() => {
    if (!newPassword) return null;
    const hasUpper = /[A-Z]/.test(newPassword);
    const hasDigit = /\d/.test(newPassword);
    const hasSpecial = /[^a-zA-Z0-9]/.test(newPassword);
    const score = [newPassword.length >= 8, hasUpper, hasDigit, hasSpecial].filter(Boolean).length;
    if (score <= 1) return { label: 'Weak', color: '#ef4444', width: '25%' };
    if (score === 2) return { label: 'Fair', color: '#f59e0b', width: '50%' };
    if (score === 3) return { label: 'Good', color: '#3b82f6', width: '75%' };
    return { label: 'Strong', color: '#16a34a', width: '100%' };
  })();

  return (
    <div style={{
      minHeight: '100vh',
      background: 'linear-gradient(135deg, #f0f0ff 0%, #faf5ff 50%, #fff 100%)',
      display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '24px',
    }}>
      <div style={{
        background: '#fff', borderRadius: '20px',
        boxShadow: '0 8px 40px rgba(79,70,229,0.10)',
        padding: '48px 40px', width: '100%', maxWidth: '420px',
      }}>
        {/* Logo */}
        <Link to="/" aria-label="iVisora — home" style={{ display: 'inline-flex', alignItems: 'center', marginBottom: 32, textDecoration: 'none' }}>
          <IvisoraLogo className="h-8" />
        </Link>

        {!token ? (
          <div style={{ textAlign: 'center' }}>
            <div style={{
              width: 64, height: 64, borderRadius: '50%', background: '#fef2f2',
              display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 20px',
            }}>
              <AlertCircle size={32} color="#dc2626" />
            </div>
            <h2 style={{ fontSize: 22, fontWeight: 700, color: '#111827', marginBottom: 8 }}>Invalid link</h2>
            <p style={{ color: '#6b7280', fontSize: 14, marginBottom: 24 }}>
              This reset link is missing required information. Please request a new one.
            </p>
            <Link to="/forgot-password" style={{
              display: 'inline-block', padding: '10px 24px',
              background: 'linear-gradient(135deg, #4f46e5, #7c3aed)',
              color: '#fff', borderRadius: 10, textDecoration: 'none', fontWeight: 600, fontSize: 14,
            }}>
              Request new link
            </Link>
          </div>
        ) : status === 'success' ? (
          <div style={{ textAlign: 'center' }}>
            <div style={{
              width: 64, height: 64, borderRadius: '50%', background: '#f0fdf4',
              display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 20px',
            }}>
              <CheckCircle size={32} color="#16a34a" />
            </div>
            <h2 style={{ fontSize: 22, fontWeight: 700, color: '#111827', marginBottom: 8 }}>
              Password updated!
            </h2>
            <p style={{ color: '#6b7280', fontSize: 14, marginBottom: 24 }}>
              Your password has been reset. Redirecting you to the home page…
            </p>
            <Link to="/" style={{ color: '#4f46e5', fontSize: 14, textDecoration: 'none', fontWeight: 600 }}>
              Go now →
            </Link>
          </div>
        ) : (
          <>
            <h1 style={{ fontSize: 24, fontWeight: 700, color: '#111827', marginBottom: 8 }}>
              Set new password
            </h1>
            <p style={{ color: '#6b7280', fontSize: 14, lineHeight: 1.6, marginBottom: 28 }}>
              Choose a strong password for your iVisora account.
            </p>

            {status === 'error' && (
              <div style={{
                display: 'flex', alignItems: 'flex-start', gap: 10,
                background: '#fef2f2', border: '1px solid #fecaca',
                borderRadius: 10, padding: '12px 14px', marginBottom: 20,
              }}>
                <AlertCircle size={16} color="#dc2626" style={{ marginTop: 1, flexShrink: 0 }} />
                <div>
                  <p style={{ color: '#dc2626', fontSize: 13, margin: '0 0 6px' }}>{errorMsg}</p>
                  {(errorMsg.includes('expired') || errorMsg.includes('used')) && (
                    <Link to="/forgot-password" style={{ color: '#dc2626', fontSize: 13, fontWeight: 600 }}>
                      Request a new link →
                    </Link>
                  )}
                </div>
              </div>
            )}

            <form onSubmit={handleSubmit}>
              {/* New password */}
              <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: '#374151', marginBottom: 6 }}>
                New password
              </label>
              <div style={{ position: 'relative', marginBottom: 8 }}>
                <Lock size={16} color="#9ca3af" style={{ position: 'absolute', left: 14, top: '50%', transform: 'translateY(-50%)' }} />
                <input
                  type={showNew ? 'text' : 'password'}
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  placeholder="At least 8 characters"
                  required
                  style={{
                    width: '100%', boxSizing: 'border-box',
                    padding: '11px 42px 11px 40px',
                    border: '1.5px solid #e5e7eb', borderRadius: 10,
                    fontSize: 14, color: '#111827', outline: 'none',
                  }}
                  onFocus={(e) => (e.target.style.borderColor = '#4f46e5')}
                  onBlur={(e) => (e.target.style.borderColor = '#e5e7eb')}
                />
                <button
                  type="button"
                  onClick={() => setShowNew((v) => !v)}
                  style={{ position: 'absolute', right: 14, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}
                >
                  {showNew ? <EyeOff size={16} color="#9ca3af" /> : <Eye size={16} color="#9ca3af" />}
                </button>
              </div>

              {/* Strength meter */}
              {strength && (
                <div style={{ marginBottom: 20 }}>
                  <div style={{ height: 4, background: '#f3f4f6', borderRadius: 2, overflow: 'hidden' }}>
                    <div style={{ height: '100%', width: strength.width, background: strength.color, borderRadius: 2, transition: 'width 0.3s, background 0.3s' }} />
                  </div>
                  <p style={{ fontSize: 12, color: strength.color, marginTop: 4, fontWeight: 600 }}>{strength.label}</p>
                </div>
              )}

              {/* Confirm password */}
              <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: '#374151', marginBottom: 6 }}>
                Confirm password
              </label>
              <div style={{ position: 'relative', marginBottom: 28 }}>
                <Lock size={16} color="#9ca3af" style={{ position: 'absolute', left: 14, top: '50%', transform: 'translateY(-50%)' }} />
                <input
                  type={showConfirm ? 'text' : 'password'}
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder="Repeat your password"
                  required
                  style={{
                    width: '100%', boxSizing: 'border-box',
                    padding: '11px 42px 11px 40px',
                    border: `1.5px solid ${confirmPassword && confirmPassword !== newPassword ? '#fca5a5' : '#e5e7eb'}`,
                    borderRadius: 10, fontSize: 14, color: '#111827', outline: 'none',
                  }}
                  onFocus={(e) => (e.target.style.borderColor = '#4f46e5')}
                  onBlur={(e) => (e.target.style.borderColor = confirmPassword && confirmPassword !== newPassword ? '#fca5a5' : '#e5e7eb')}
                />
                <button
                  type="button"
                  onClick={() => setShowConfirm((v) => !v)}
                  style={{ position: 'absolute', right: 14, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}
                >
                  {showConfirm ? <EyeOff size={16} color="#9ca3af" /> : <Eye size={16} color="#9ca3af" />}
                </button>
              </div>

              <button
                type="submit"
                disabled={status === 'loading'}
                style={{
                  width: '100%', padding: '12px',
                  background: status === 'loading' ? '#a5b4fc' : 'linear-gradient(135deg, #4f46e5, #7c3aed)',
                  color: '#fff', border: 'none', borderRadius: 10,
                  fontSize: 15, fontWeight: 600, cursor: status === 'loading' ? 'not-allowed' : 'pointer',
                  display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
                }}
              >
                {status === 'loading' ? (
                  <><Loader2 size={16} style={{ animation: 'spin 1s linear infinite' }} /> Updating…</>
                ) : 'Update password'}
              </button>
            </form>

            <div style={{ textAlign: 'center', marginTop: 24 }}>
              <Link
                to="/"
                style={{ display: 'inline-flex', alignItems: 'center', gap: 6, color: '#6b7280', fontSize: 14, textDecoration: 'none' }}
              >
                <ArrowLeft size={14} /> Back to home
              </Link>
            </div>
          </>
        )}
      </div>

      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );
};

export default ResetPasswordPage;
